/**
 * Google Analytics 4 integration
 */

import type { AnalyticsUser, EcommerceItem } from './types';
import { getFirstLandingPage } from './firstTouch';

// Ensure gtag is available on window and returns a callable function
function ensureGtag(): (...args: unknown[]) => void {
  const w = window as typeof window & { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };
  w.dataLayer = w.dataLayer || [];
  if (!w.gtag) {
    w.gtag = function gtag(...args: unknown[]) {
      w.dataLayer!.push(args);
    };
  }
  return w.gtag;
}

/**
 * Initialize GA4 with measurement ID
 * Note: send_page_view is false because we track pages manually for SPA
 */
export function init(measurementId: string): void {
  const gtag = ensureGtag();
  gtag('js', new Date());
  gtag('config', measurementId, {
    send_page_view: false, // Manual page tracking for SPA
  });
  // Default all sessions to anonymous; identify() flips this to true on login.
  // Gives a clean logged-in vs anonymous cut in GA4 without user_id gymnastics.
  gtag('set', 'user_properties', { logged_in: false });
}

/**
 * Identify user and set user properties
 */
export function identify(user: AnalyticsUser): void {
  const gtag = ensureGtag();
  // Set user ID for cross-session tracking
  gtag('set', 'user_id', user.userId);

  // Set user properties for segmentation.
  // NOTE: github_login is deliberately NOT sent to GA4 - it is a personal
  // identifier (GA4 prohibits PII, and it clashes with our anti-surveillance
  // stance). It stays in PostHog only.
  gtag('set', 'user_properties', {
    org_id: user.orgId || '',
    org_slug: user.orgSlug || '',
    org_name: user.orgName || '',
    is_personal: user.isPersonal != null ? user.isPersonal : '',
    subscription_tier: user.subscriptionTier,
    is_trial: user.isTrial,
    trial_days_remaining: user.trialDaysRemaining,
    signup_date: user.signupDate || '',
    first_landing_page: getFirstLandingPage() || '',
    logged_in: true,
  });
}

/**
 * Track a custom event
 */
export function trackEvent(
  name: string,
  params?: Record<string, string | number | boolean | undefined>
): void {
  const gtag = ensureGtag();
  gtag('event', name, params);
}

/**
 * Track page view (manual for SPA)
 */
export function trackPageView(
  path: string,
  title: string,
  additionalParams?: Record<string, string | number | boolean | undefined>
): void {
  const gtag = ensureGtag();
  // Set page properties globally before firing the event.
  // GA4's "Page title and screen name" dimension uses the global page_title,
  // not the event parameter - this ensures the dimension is populated.
  gtag('set', { page_title: title, page_path: path });
  gtag('event', 'page_view', {
    page_path: path,
    page_title: title,
    page_location: window.location.href,
    ...additionalParams,
  });
}

/**
 * Track ecommerce begin_checkout event (GA4 recommended event)
 */
export function trackBeginCheckout(
  value: number,
  currency: string,
  items: EcommerceItem[]
): void {
  const gtag = ensureGtag();
  gtag('event', 'begin_checkout', {
    currency,
    value,
    items,
  });
}

/**
 * Track ecommerce purchase event (GA4 recommended event)
 */
export function trackPurchase(
  transactionId: string,
  value: number,
  currency: string,
  items: EcommerceItem[]
): void {
  const gtag = ensureGtag();
  gtag('event', 'purchase', {
    transaction_id: transactionId,
    currency,
    value,
    items,
  });
}

/**
 * Clear user data (on logout)
 */
export function reset(): void {
  const gtag = ensureGtag();
  gtag('set', 'user_id', null);
  gtag('set', 'user_properties', {
    org_id: '',
    org_slug: '',
    org_name: '',
    is_personal: '',
    subscription_tier: '',
    is_trial: false,
    trial_days_remaining: 0,
    signup_date: '',
    logged_in: false,
  });
}
